// src/components/DynamicSetupWizard/Components/DropdownSelector.tsx
import React from "react";
import Select from "react-select";
import { Alert } from "reactstrap";

interface DropdownSelectorProps<T> {
  title: string;
  description: string;
  items: T[];
  idField: keyof T;
  labelField: keyof T;
  loading: boolean;
  error?: string;
  pending: T | null;
  onSelect: (item: T) => void;
}

type OptionType = { value: string; label: string };

export default function DropdownSelector<T extends Record<string, any>>({
  title,
  description,
  items,
  idField,
  labelField,
  loading,
  error,
  pending,
  onSelect
}: DropdownSelectorProps<T>) {
  const options: OptionType[] = items.map(i => ({
    value: String(i[idField]),
    label: String(i[labelField])
  }));
  const selected = pending
    ? { value: String(pending[idField]), label: String(pending[labelField]) }
    : null;

  return (
    <>
      <h5 className="fw-bold">{title}</h5>
      <p className="text-secondary mb-4">{description}</p>

      {error && <Alert color="danger">{error}</Alert>}

      {loading ? (
        // skeleton placeholder
        <div className="mb-4 placeholder-glow">
          <span
            className="placeholder w-100"
            style={{ display: "block", height: "2.5rem", borderRadius: "0.25rem" }}
          ></span>
        </div>
      ) : (
        <Select<OptionType, false>
          className="mb-4"
          options={options}
          value={selected}
          placeholder="Search and select…"
          isSearchable
          onChange={(opt: { value: string; }) => {
            if (opt) {
              const found = items.find(i => String(i[idField]) === opt.value)!;
              onSelect(found);
            }
          }}
          noOptionsMessage={() => "No results"}
          styles={{
            control: (base: any) => ({
              ...base,
              boxShadow: "none",
              borderRadius: 4,
              borderColor: "#ced4da",
              "&:hover": { borderColor: "#adb5bd" }
            })
          }}
        />
      )}
    </>
  );
}
